import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { clearApiCache } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// IndexedDB helpers for offline auth persistence
const AUTH_DB_NAME = 'mytrip-auth';
const AUTH_DB_VERSION = 1;

const openAuthDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(AUTH_DB_NAME, AUTH_DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('auth')) {
        db.createObjectStore('auth', { keyPath: 'key' });
      }
    };
  });
};

const saveAuthToIDB = async (user, token) => {
  try {
    const db = await openAuthDB();
    const tx = db.transaction('auth', 'readwrite');
    const store = tx.objectStore('auth');
    store.put({ key: 'currentUser', user, token, savedAt: new Date().toISOString() });
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
    console.log('[Auth] Saved auth to IndexedDB');
  } catch (error) {
    console.error('[Auth] Failed to save auth to IndexedDB:', error);
  }
};

const getAuthFromIDB = async () => {
  try {
    const db = await openAuthDB();
    const tx = db.transaction('auth', 'readonly');
    const store = tx.objectStore('auth');
    const result = await new Promise((resolve, reject) => {
      const request = store.get('currentUser');
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    db.close();
    return result || null;
  } catch (error) {
    console.error('[Auth] Failed to get auth from IndexedDB:', error);
    return null;
  }
};

const clearAuthFromIDB = async () => {
  try {
    const db = await openAuthDB();
    const tx = db.transaction('auth', 'readwrite');
    const store = tx.objectStore('auth');
    store.delete('currentUser');
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
    console.log('[Auth] Cleared auth from IndexedDB');
  } catch (error) {
    console.error('[Auth] Failed to clear auth from IndexedDB:', error);
  }
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isOfflineAuth, setIsOfflineAuth] = useState(false);

  // Configure axios with token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [token]);

  // Check if user is logged in on mount - with offline support
  const checkAuth = useCallback(async () => {
    if (!token) {
      // No token in localStorage - check IndexedDB for offline auth
      const cachedAuth = await getAuthFromIDB();
      if (cachedAuth && cachedAuth.user && cachedAuth.token) {
        console.log('[Auth] Found cached auth in IndexedDB, using offline mode');
        setToken(cachedAuth.token);
        setUser(cachedAuth.user);
        setIsOfflineAuth(true);
        setLoading(false);
        return;
      }
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setIsOfflineAuth(false);
      // Save to IndexedDB for offline access
      await saveAuthToIDB(response.data, token);
    } catch (error) {
      // Check if this is a network error vs auth error
      const isNetworkError = !error.response || error.message === 'Network Error';
      const isAuthError = error.response?.status === 401 || error.response?.status === 403;
      
      if (isNetworkError) {
        // Network error - try to use cached auth
        console.log('[Auth] Network error during auth check, trying cached auth');
        const cachedAuth = await getAuthFromIDB();
        if (cachedAuth && cachedAuth.user) {
          console.log('[Auth] Using cached auth for offline mode');
          setUser(cachedAuth.user);
          setIsOfflineAuth(true);
        } else {
          console.log('[Auth] No cached auth available');
          // Keep the token but show as offline with limited access
          setIsOfflineAuth(true);
        }
      } else if (isAuthError) {
        // Actual auth error (invalid/expired token) - clear everything
        console.log('[Auth] Auth error, clearing credentials');
        setToken(null);
        setUser(null);
        await clearAuthFromIDB();
      } else {
        // Other server error - try cached auth
        console.log('[Auth] Server error, trying cached auth');
        const cachedAuth = await getAuthFromIDB();
        if (cachedAuth && cachedAuth.user) {
          setUser(cachedAuth.user);
          setIsOfflineAuth(true);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setToken(response.data.access_token);
    setUser(response.data.user);
    setIsOfflineAuth(false);
    // Save to IndexedDB for offline access
    await saveAuthToIDB(response.data.user, response.data.access_token);
    return response.data;
  };

  const register = async (name, email, password, referralCode = '') => {
    const payload = { name, email, password };
    if (referralCode) {
      payload.referral_code = referralCode.toUpperCase();
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    setToken(response.data.access_token);
    setUser(response.data.user);
    setIsOfflineAuth(false);
    // Save to IndexedDB for offline access
    await saveAuthToIDB(response.data.user, response.data.access_token);
    return response.data;
  };

  const logout = async () => {
    setToken(null);
    setUser(null);
    setIsOfflineAuth(false);
    clearApiCache();
    await clearAuthFromIDB();
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setIsOfflineAuth(false);
      // Update IndexedDB
      await saveAuthToIDB(response.data, token);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isOfflineAuth,  // New: indicates we're using cached auth
    login,
    register,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
